import assert from 'assert';
import {useState, useMemo, useCallback} from 'react';

import {isPublishingVersion} from 'notebook/version-history/VersionHistoryPanelUtil';
import {Version} from 'notebook/version-history/VersionHistoryTypes';

export interface UsePublishingVersionDropdowns {
  hasPublishingVersions: boolean;
  isPublishingVersionExpanded: (versionId: number) => boolean;
  togglePublishingVersion: (versionId: number) => void;
  shouldShowPublishingVersionToggle: (version: Version) => boolean;
  isVersionShown: (versionId: number) => boolean;
  expandAll: () => void;
  collapseAll: () => void;
}

/**
 * Provide state variables that control the expand/collapse of publishing version dropdowns
 * @param versions array of versions. Function assumes
 *    1. versions are sorted descending by modified time
 *    2. there is at least one version in version array
 */
export function usePublishingVersionDropdowns(
  versions: Array<Version>,
  openVersionId: number
): UsePublishingVersionDropdowns {
  assert(versions.length > 0, 'versions array must have at least one version');

  const hasPublishingVersions = useMemo(
    () => versions.some((version) => version.publishableVersionInfo),
    [versions]
  );

  const publishingVersions: Array<Version> = useMemo(
    () => versions.filter((version) => isPublishingVersion(version)),
    [versions]
  );
  const publishingVersionIds: Set<number> = useMemo(
    () => new Set(publishingVersions.map((version) => version.id)),
    [publishingVersions]
  );
  // map draft versions to publishing versions
  const draftVersionIdToPublishingVersionId: Record<number, number> = useMemo(() => {
    const tmp: Record<number, number> = {};
    let publishingVersionId = versions[0].id;
    for (const version of versions) {
      if (isPublishingVersion(version)) {
        publishingVersionId = version.id;
      } else {
        tmp[version.id] = publishingVersionId;
      }
    }
    return tmp;
  }, [versions]);

  const publishingVersionIdsWithDraftVersions: Set<number> = useMemo(() => {
    const tmp: Set<number> = new Set();
    for (const publishingVersionId of Object.values(draftVersionIdToPublishingVersionId)) {
      tmp.add(publishingVersionId);
    }
    return tmp;
  }, [draftVersionIdToPublishingVersionId]);

  // map publishing version IDs to boolean
  const [publishingVersionToggles, setPublishingVersionToggles] = useState<Record<number, boolean>>(() =>
    Object.fromEntries(
      publishingVersions.map((version) => {
        // Expand the parent publishing version if the open version is a draft version
        const isExpanded = draftVersionIdToPublishingVersionId[openVersionId] === version.id;
        return [version.id, isExpanded];
      })
    )
  );

  const isPublishingVersionExpanded = useCallback(
    (versionId: number): boolean => publishingVersionToggles[versionId] || false,
    [publishingVersionToggles]
  );

  const togglePublishingVersion = useCallback(
    (versionId: number): void => {
      setPublishingVersionToggles((prev) => ({...prev, [versionId]: !prev[versionId]}));
    },
    [setPublishingVersionToggles]
  );

  const shouldShowPublishingVersionToggle = useCallback(
    (version: Version): boolean =>
      isPublishingVersion(version) &&
      (publishingVersionIdsWithDraftVersions.has(version.id) ||
        Boolean(version.publishableVersionInfo?.versionDescription)),
    [publishingVersionIdsWithDraftVersions]
  );

  const isVersionShown = (versionId: number): boolean => {
    // if no publishing versions, show all versions
    if (!hasPublishingVersions) {
      return true;
    }
    // if version is a publishing version, show it
    if (publishingVersionIds.has(versionId)) {
      return true;
    }
    // otherwise, determine if the publishingVersion of the draftVersion is expanded
    const publishingVersionId = draftVersionIdToPublishingVersionId[versionId];
    return isPublishingVersionExpanded(publishingVersionId);
  };

  const expandAll = (): void => {
    setPublishingVersionToggles(() =>
      Object.fromEntries(publishingVersions.map((version) => [version.id, true]))
    );
  };

  const collapseAll = (): void => {
    setPublishingVersionToggles(() =>
      Object.fromEntries(publishingVersions.map((version) => [version.id, false]))
    );
  };

  return {
    hasPublishingVersions,
    isPublishingVersionExpanded,
    togglePublishingVersion,
    shouldShowPublishingVersionToggle,
    isVersionShown,
    expandAll,
    collapseAll,
  };
}
